import React, { useEffect } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { useIsReducedMotion } from '@atlaskit/motion';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import {
	ReducedMotionRovoGeneratingAnimation,
	RovoGeneratingAnimation,
} from '../../../ai-answer-dialog/ai-answer-loading/rovo-generating-animation';
import { onAIAnswerLoadingDialogShown } from '../../../ai-answer-dialog/analytics';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../../common/utils/sain-feature-control';
import { QueryIntent } from '../../../constants';
import { messages } from '../../smart-answer-card/messages';

import RovoIcon from './assets/Rovo.svg';

const styles = cssMap({
	containerNewSAINLoading: {
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		height: '198px',
	},
	inline: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingTop: token('space.100'),
	},
	inlineAnimation: {
		display: 'inline-flex',
		verticalAlign: 'middle',
	},
	jiraNlToJqlCardHeightOverride: {
		height: '382px',
	},
	navigationalCardHeightOverride: {
		height: '280px',
	},
	explicitQuestion: {
		height: '350px',
	},
	standardSAINHeightOverride: {
		height: '214px',
		paddingTop: token('space.0'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
	},
	luminaHeightOverride: {
		height: '352px',
		paddingTop: token('space.0'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
	},
});

type SmartAnswerCardBodyGeneratingProps = {
	heightOverride?: 'jiraNlToJqlCard' | 'navigationalCard' | 'lumina';
	queryIntent?: QueryIntent;
};
export function SmartAnswerCardBodyGenerating({
	heightOverride,
	queryIntent,
}: SmartAnswerCardBodyGeneratingProps): React.JSX.Element {
	const { commonAttributes, queryReasoning } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const isReducedMotion = useIsReducedMotion();

	// TODO: Temporary function for TEAMEU - normalize reasoning strings by lowercasing first letter and removing trailing periods
	const normalizeReasoningString = (str: string): string => {
		if (!str) {
			return str;
		}
		let result = `${str.charAt(0).toLowerCase()}${str.slice(1)}`;
		// Remove trailing period if it exists
		if (result.endsWith('.')) {
			result = result.slice(0, -1);
		}
		return result;
	};

	useEffect(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Stack
			xcss={cx(
				styles.containerNewSAINLoading,
				heightOverride === 'jiraNlToJqlCard' && styles.jiraNlToJqlCardHeightOverride,
				heightOverride === 'navigationalCard' && styles.navigationalCardHeightOverride,
				!heightOverride &&
					SAINFeatureControl.isLuminaEnabled() &&
					styles.standardSAINHeightOverride,
				heightOverride === 'lumina' &&
					SAINFeatureControl.isLuminaEnabled() &&
					styles.luminaHeightOverride,
				queryIntent === QueryIntent.EXPLICIT_QUESTION && styles.explicitQuestion,
			)}
			space="space.200"
		>
			<Inline space={'space.100'} xcss={styles.inline} alignBlock="start">
				<Image src={RovoIcon} alt="" />
				<Text color={'color.text.subtle'}>
					{queryReasoning ? (
						<FormattedMessage
							{...messages.smart_answer_card_rovo_generating}
							values={{ queryReasoning: normalizeReasoningString(queryReasoning) }}
						/>
					) : (
						<FormattedMessage {...messages.smart_answer_card_rovo_is_thinking} />
					)}
					<Box as="span" xcss={styles.inlineAnimation}>
						{isReducedMotion ? (
							<ReducedMotionRovoGeneratingAnimation />
						) : (
							<RovoGeneratingAnimation />
						)}
					</Box>
				</Text>
			</Inline>
			<Skeleton
				width="100%"
				height="16px"
				color={token('color.skeleton.subtle')}
				borderRadius={'6px'}
			/>
			<Skeleton
				width="100%"
				height="16px"
				color={token('color.skeleton.subtle')}
				borderRadius={'6px'}
			/>
			{SAINFeatureControl.isLuminaEnabled() ? (
				<Skeleton
					width="75%"
					height="16px"
					color={token('color.skeleton.subtle')}
					borderRadius={'6px'}
				/>
			) : (
				<Skeleton
					width="90%"
					height="16px"
					color={token('color.skeleton.subtle')}
					borderRadius={'6px'}
				/>
			)}
			{heightOverride === 'lumina' && SAINFeatureControl.isLuminaEnabled() && (
				<Inline space="space.100">
					<Skeleton
						width="256px"
						height="166px"
						color={token('color.skeleton.subtle')}
						borderRadius={'6px'}
					/>
					<Skeleton
						width="256px"
						height="166px"
						color={token('color.skeleton.subtle')}
						borderRadius={'6px'}
					/>
					<Skeleton
						width="256px"
						height="166px"
						color={token('color.skeleton.subtle')}
						borderRadius={'6px'}
					/>
				</Inline>
			)}
		</Stack>
	);
}
